import type { CareSeekerShallowLeadFormData } from "~care/types/forms/care-seeker-shallow-lead-form";

import { cn } from "@homeinstead/ui";
import { Controller, useFormContext } from "react-hook-form";
import { Button } from "../../Button/Button";
import { FormError } from "../../FormError/FormError";
import { Input } from "../../Input/Input";
import { Link } from "../../Link/Link";
import { PhoneInput } from "../../PhoneInput/PhoneInput";
import { Checkbox } from "../Checkbox";
import { Testimony } from "./Testimony";
import { testimonies } from "./testimonies";

export const ContactStep = ({
  isPending,
  className,
}: {
  isPending: boolean;
  className?: string;
}) => {
  const { register, control, formState } =
    useFormContext<CareSeekerShallowLeadFormData>();
  const { errors, isValid, touchedFields } = formState;

  return (
    <div className={cn("flex h-full flex-col gap-4 xl:flex-row", className)}>
      <div className="flex flex-col gap-4 md:w-[440px]">
        <h1 className="text-left font-editorial-new font-light text-1xl">
          Almost done!
        </h1>
        <p className="text-base text-new-beginnings-base">
          Please fill in your contact details and one of our Care Consultants
          will reach out to you shortly.
        </p>

        {/* Text fields */}
        <div>
          <Input
            placeholder="First Name"
            type="text"
            {...register("step5.firstName")}
            className="bg-white text-base text-new-beginnings-base"
          />
          {touchedFields.step5?.firstName && errors.step5?.firstName && (
            <FormError.span>{errors.step5.firstName.message}</FormError.span>
          )}
        </div>

        <div>
          <Input
            placeholder="Last Name"
            type="text"
            {...register("step5.lastName")}
            className="bg-white text-base text-new-beginnings-base"
          />
          {touchedFields.step5?.lastName && errors.step5?.lastName && (
            <FormError.span>{errors.step5.lastName.message}</FormError.span>
          )}
        </div>

        <div>
          <Input
            placeholder="Email Address"
            type="email"
            {...register("step5.email")}
            className="bg-white text-base text-new-beginnings-base"
          />
          {touchedFields.step5?.email && errors.step5?.email && (
            <FormError.span>{errors.step5.email.message}</FormError.span>
          )}
        </div>

        <PhoneInput<CareSeekerShallowLeadFormData>
          className="bg-white text-base text-new-beginnings-base"
          fieldName={"step5.phone"}
        />

        {/**
         * Checkboxes - consentTerms
         */}

        <Controller
          control={control}
          name="step5.consentTerms"
          render={({ field }) => (
            <div className="flex items-start gap-2">
              <Checkbox
                checked={field.value}
                id="consentTerms"
                name={field.name}
                onChange={field.onChange}
              />
              <label
                className="text-new-beginnings-base text-xs leading-5"
                htmlFor="consentTerms"
              >
                <div>
                  By checking this box, I agree to receive SMS text messages
                  from Home Instead at the phone number provided. Message & data
                  rates may apply. Message frequency may vary based on service
                  needs and SMS activity. Reply STOP to opt out of further
                  messaging. For more details, including our SMS terms, please
                  refer to our{" "}
                  <Link
                    className="text-xs underline hover:no-underline"
                    href="https://www.homeinstead.com/legal/privacy-policy"
                    rel="noopener noreferrer"
                    size="inline"
                    target="_blank"
                    variant="plain"
                  >
                    Privacy Policy
                  </Link>
                  .
                </div>
              </label>
            </div>
          )}
        />
        {touchedFields.step5?.consentTerms && errors.step5?.consentTerms && (
          <FormError.span>{errors.step5.consentTerms.message}</FormError.span>
        )}

        <Button
          className="mt-4 h-16 w-full rounded-full md:h-12"
          disabled={!isValid || isPending}
          type="submit"
        >
          Contact me with care options
        </Button>

        <span className="text-new-beginnings-base text-xs leading-5">
          By clicking &quot;Contact me with care options&quot; you agree to our{" "}
          <Link
            className="text-xs underline hover:no-underline"
            href="https://www.homeinstead.com/legal/client-notice-of-privacy-practices"
            rel="noopener noreferrer"
            size="inline"
            target="_blank"
            variant="plain"
          >
            Privacy Policy
          </Link>
          .
        </span>
      </div>
      <Testimony className="mb-auto ml-auto hidden max-w-md flex-col justify-end gap-4 xl:flex">
        {testimonies.step5}
      </Testimony>
    </div>
  );
};
